/**
 * Cutfruit Design System — floating add-to-cart bar controller.
 * Drives sections/ds-floating-atc.liquid. Owns NO state: mirrors and drives
 * snippets/product-block-ds-purchase-panel.liquid (assets/ds-purchase-panel.js)
 * via native DOM events + the same form. Pack options are read live from the
 * panel's rendered segmented control, so the bar's option labels can never
 * drift from the PDP's — there is no separate copy of that data here.
 */
(function () {
  function getBar() {
    return document.querySelector('[data-ds-floating-atc]');
  }

  function getPanel() {
    return document.querySelector('[data-ds-purchase-panel]');
  }

  function getForm(panel) {
    return panel.closest('form') || panel.querySelector('form');
  }

  function buildPackItems(panel) {
    const segments = Array.from(
      panel.querySelectorAll('[data-ds-segmented-control-segment]')
    );
    return segments.map((segment) => {
      const input = segment.querySelector('.ds-segmented-control__input');
      const labelEl = segment.querySelector('.ds-segmented-control__label');
      const value = segment.getAttribute('data-value');
      const savePct = computeSavePct(panel, value);
      const baseLabel = labelEl ? labelEl.textContent.trim() : value;
      return {
        value,
        label: baseLabel,
        // Trigger shows "<label>/mo" (matches Figma); dropdown options stay plain.
        triggerLabel: `${baseLabel}/mo`,
        meta: savePct > 0 ? `(Save ${savePct}%)` : '',
        selected: !!(input && input.checked),
      };
    });
  }

  function getConfig(panel) {
    const el = panel.querySelector('[data-ds-purchase-panel-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('ds-floating-atc: invalid purchase panel config JSON', e);
      return null;
    }
  }

  function currentMode(panel) {
    const checked = panel.querySelector('input[name="purchase_type"]:checked');
    return checked ? checked.value : 'subscribe';
  }

  /**
   * Mirrors assets/ds-purchase-panel.js's applyStackedDiscount so the
   * trigger's "(Save X%)" always matches the panel's own tag text, without
   * duplicating cart math beyond this single percentage calculation.
   */
  function computeSavePct(panel, qtyValue) {
    const config = getConfig(panel);
    if (!config) return 0;

    const qty = parseInt(qtyValue, 10) || 1;
    const mode = currentMode(panel);
    const isSubscribe = mode === 'subscribe';

    const unitCents = isSubscribe ? config.subCents || 0 : config.oneTimeCents || 0;
    const compareUnitCents = isSubscribe
      ? config.subCompareCents || config.oneTimeCents || 0
      : config.oneTimeCents || 0;

    const total = unitCents * qty;

    // Both order-level discounts apply to the same base and sum into one
    // combined rate rather than compounding — see applyStackedDiscount in
    // ds-purchase-panel.js for the verified real-cart breakdown.
    let orderDiscountPct = isSubscribe ? config.firstPurchaseDiscountPct || 0 : 0;
    if (qty >= 2) {
      orderDiscountPct += config.multiPackDiscountPct || 0;
    }
    const discountedTotal = Math.ceil(total * (1 - orderDiscountPct / 100));

    const compareTotal = compareUnitCents * qty;
    if (compareTotal <= 0 || discountedTotal >= compareTotal) return 0;
    return Math.round(((compareTotal - discountedTotal) / compareTotal) * 100);
  }

  function formatLabelPattern(pattern, baseText, priceText) {
    return pattern.replace('[base]', baseText).replace('[price]', priceText);
  }

  function readActivePriceText(panel) {
    const mode = currentMode(panel);
    const card = panel.querySelector(
      `[data-ds-radio-card][data-value="${mode === 'subscribe' ? 'subscribe' : 'onetime'}"]`
    );
    const priceEl = card && card.querySelector('.ds-radio-card__price-current');
    return priceEl ? priceEl.textContent.trim() : '';
  }

  function syncAtcButton(bar, panel) {
    const mainBtn = panel.querySelector('[data-ds-atc-button]');
    const barBtn = bar.querySelector('[data-ds-floating-atc-atc]');
    const barLabel = bar.querySelector('[data-ds-floating-atc-atc-label]');
    if (!mainBtn || !barBtn || !barLabel) return;

    const disabled = mainBtn.disabled;
    barBtn.disabled = disabled;
    barBtn.classList.toggle('cursor-not-allowed', disabled);

    // The floating bar's CTA text is its own setting-driven strings (never
    // mirrors the main button's own copy, e.g. "Pre-order now") — but does
    // mirror the SAME Add to cart / Pre-order / Sold Out state, using the
    // is-preorder flag Liquid computed the same way as the purchase panel.
    const pattern = bar.getAttribute('data-atc-label-pattern') || '[base] - [price]/mo';
    const addToCartText = bar.getAttribute('data-add-to-cart-text') || barLabel.textContent.trim();
    const preorderText = bar.getAttribute('data-preorder-text') || addToCartText;
    const soldOutText = bar.getAttribute('data-sold-out-text') || preorderText;
    const isPreOrder = bar.getAttribute('data-is-preorder') === 'true';
    const baseText = disabled ? soldOutText : isPreOrder ? preorderText : addToCartText;

    if (disabled) {
      barLabel.textContent = baseText;
      return;
    }

    const priceText = readActivePriceText(panel);
    barLabel.textContent = priceText
      ? formatLabelPattern(pattern, baseText, priceText)
      : baseText;
  }

  function refreshBar(bar, panel) {
    const select = bar.querySelector('[data-ds-select]');
    if (select) {
      const items = buildPackItems(panel);
      select.dispatchEvent(
        new CustomEvent('ds-select:options', { detail: { items } })
      );
    }
    syncAtcButton(bar, panel);
  }

  function wireSelectToForm(bar, panel) {
    const select = bar.querySelector('[data-ds-select]');
    if (!select) return;

    select.addEventListener('ds-select:change', (event) => {
      const qty = event.detail && event.detail.value;
      if (!qty) return;
      const segments = panel.querySelectorAll('[data-ds-segmented-control-segment]');
      segments.forEach((segment) => {
        if (segment.getAttribute('data-value') === String(qty)) {
          const input = segment.querySelector('.ds-segmented-control__input');
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });

    document.addEventListener('pack:change', (event) => {
      const qty = event.detail && event.detail.qty;
      if (qty == null) return;
      select.dispatchEvent(
        new CustomEvent('ds-select:set', { detail: { value: String(qty) } })
      );
      refreshBar(bar, panel);
    });

    document.addEventListener('purchase-mode:change', () => {
      refreshBar(bar, panel);
    });
  }

  function wireAtcButton(bar, panel) {
    const barBtn = bar.querySelector('[data-ds-floating-atc-atc]');
    const form = getForm(panel);
    if (!barBtn || !form) return;

    barBtn.addEventListener('click', () => {
      if (barBtn.disabled) return;
      form.requestSubmit();
    });
  }

  function observeAtcButtonState(bar, panel) {
    const mainBtn = panel.querySelector('[data-ds-atc-button]');
    if (!mainBtn) return;

    const observer = new MutationObserver(() => syncAtcButton(bar, panel));
    observer.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function observeVisibility(bar, panel) {
    const atcButton = panel.querySelector('[data-ds-atc-button]');
    const footer = document.querySelector('footer');
    const state = {
      scrolledPast: window.scrollY > window.innerHeight,
      buyBoxButtonVisible: false,
      footerVisible: false,
    };

    function updateVisibility() {
      const shouldShow = state.scrolledPast && !state.buyBoxButtonVisible && !state.footerVisible;
      bar.classList.toggle('ds-floating-atc--visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    window.addEventListener(
      'scroll',
      () => {
        state.scrolledPast = window.scrollY > window.innerHeight;
        updateVisibility();
      },
      { passive: true }
    );

    if (atcButton) {
      const observer = new IntersectionObserver((entries) => {
        state.buyBoxButtonVisible = entries[0].isIntersecting;
        updateVisibility();
      });
      observer.observe(atcButton);
    }

    if (footer) {
      const footerObserver = new IntersectionObserver((entries) => {
        state.footerVisible = entries[0].isIntersecting;
        updateVisibility();
      });
      footerObserver.observe(footer);
    }

    updateVisibility();
  }

  function init() {
    const bar = getBar();
    const panel = getPanel();
    if (!bar || !panel) return;
    if (bar.dataset.dsFloatingAtcInit) return;
    bar.dataset.dsFloatingAtcInit = '1';

    refreshBar(bar, panel);
    wireSelectToForm(bar, panel);
    wireAtcButton(bar, panel);
    observeAtcButtonState(bar, panel);
    observeVisibility(bar, panel);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', init);
})();
